// plan-09 S03: mourning mode + 48-second silence ritual — bible §6, §36

import { load, set } from '../core/save'
import { setPortalAmpMultiplier } from '../postgrade/passes/standingWave'
import { createStreamTarget } from '../oracle/streaming'
import type { CommandCtx } from '../oracle/commands'

const MAX_RITUALS = 9
const SILENCE_DURATION_MS = 48000
const MOURNING_DURATION_MS = 4800

// ─── Mourning mode ───

let mourningActive = false

export async function triggerMourning(ctx: CommandCtx): Promise<void> {
  if (mourningActive) return
  mourningActive = true

  // dim terminal text
  const history = ctx.history
  history.style.opacity = '0.5'
  history.style.transition = 'opacity 600ms'

  // standing wave amplitude doubles for 4.8s
  setPortalAmpMultiplier(2)

  // no text streams during mourning — the mourning IS the text

  setTimeout(() => {
    // return to normal
    history.style.opacity = '1'
    setPortalAmpMultiplier(1)

    // stream the post-mourning line
    const line = createStreamTarget(history, 'vt-line-oracle')
    line.style.color = 'var(--vt-king-green)'
    line.textContent = '> ...'

    setTimeout(() => {
      mourningActive = false
    }, 400)
  }, MOURNING_DURATION_MS)
}

// ─── 48-second silence ritual ───

let silenceTimer: ReturnType<typeof setTimeout> | null = null
let lastInputTime = performance.now()
let watching = false

function resetSilence(): void {
  lastInputTime = performance.now()
  if (silenceTimer !== null) {
    clearTimeout(silenceTimer)
    silenceTimer = null
  }
  armSilenceTimer()
}

function armSilenceTimer(): void {
  if (silenceTimer !== null) return

  const now = performance.now()
  const elapsed = now - lastInputTime
  const remaining = SILENCE_DURATION_MS - elapsed

  if (remaining <= 0) {
    // already been 48s since last input — schedule async check to avoid stack overflow
    silenceTimer = setTimeout(() => {
      silenceTimer = null
      checkSilenceConditions()
    }, 1000)
    return
  }

  silenceTimer = setTimeout(() => {
    silenceTimer = null
    checkSilenceConditions()
  }, remaining)
}

function checkSilenceConditions(): void {
  const s = load()

  // conditions: audio unmuted, focus held, no input in 48s
  if (s.audioMuted) { armSilenceTimer(); return }
  if (!document.hasFocus()) { armSilenceTimer(); return }
  if (performance.now() - lastInputTime < SILENCE_DURATION_MS - 100) { armSilenceTimer(); return }

  // ritual fires
  const count = s.silenceRitualCompleted
  if (count < MAX_RITUALS) {
    s.silenceRitualCompleted++
    set('silenceRitualCompleted', s.silenceRitualCompleted)
  }

  // stream the line
  const history = document.querySelector('.vt-history') as HTMLElement | null
  if (!history) return

  const line = document.createElement('div')
  line.className = 'vt-line-oracle'
  line.style.color = 'var(--vt-amber)'

  if (count === MAX_RITUALS - 1) {
    // 9th ritual (0-indexed: 8 means the NEXT one is #9)
    line.textContent = 'the kingdom remembers you.'
  } else {
    line.textContent = 'the kingdom holds its breath.'
  }

  history.appendChild(line)

  // trigger mourning mode
  triggerMourningFromRitual(history)
}

function triggerMourningFromRitual(history: HTMLElement): void {
  if (mourningActive) return
  mourningActive = true

  history.style.opacity = '0.5'
  history.style.transition = 'opacity 600ms'
  setPortalAmpMultiplier(2)

  setTimeout(() => {
    history.style.opacity = '1'
    setPortalAmpMultiplier(1)

    const line = createStreamTarget(history, 'vt-line-oracle')
    line.style.color = 'var(--vt-king-green)'
    line.textContent = '> ...'

    setTimeout(() => {
      mourningActive = false
    }, 400)
  }, MOURNING_DURATION_MS)
}

// ─── Start/stop watching ───

export function startSilenceWatch(): void {
  if (watching) return
  watching = true

  const events = ['keydown', 'mousedown', 'scroll', 'touchstart'] as const
  const handler = () => resetSilence()

  // also track mousemove with delta > 4px
  let lastX = 0
  let lastY = 0
  const mouseHandler = (e: MouseEvent) => {
    const dx = Math.abs(e.clientX - lastX)
    const dy = Math.abs(e.clientY - lastY)
    lastX = e.clientX
    lastY = e.clientY
    if (dx > 4 || dy > 4) {
      resetSilence()
    }
  }

  for (const ev of events) {
    document.addEventListener(ev, handler, { passive: true })
  }
  document.addEventListener('mousemove', mouseHandler, { passive: true })

  armSilenceTimer()
}
